/**
 * The UP/DOWN control at the top of the ticket — components.md §2.3.
 *
 * Two equal halves at `control-height-lg`, `radius-md`, 1px `border-default`.
 * The selected half takes its outcome's solid fill; the unselected half is
 * `bg-raised`/`text-secondary` and hovers into that outcome's tint.
 *
 * **The toggle doubles as the odds display.** Each half carries its live price
 * in cents, which is why this is not a `<SegmentedControl>` with two segments:
 * that control's `sub` slot is a static label, and these two numbers change ten
 * times a second and must flash on **change of rendered value, never on
 * receipt** (invariant 5). `<NumCell>` owns that rule, so each half wraps one.
 *
 * Colour is never the only carrier: the glyph and the word are both present, and
 * the two halves are in a fixed left/right order (design-system §2.5).
 *
 * **The keyboard model is `radio-group.ts`'s, since Phase 10.** One tab stop for
 * the pair and the arrows switch outcome — on the one control in the product
 * where getting that wrong means a keyboard user Tabs past DOWN on the way to
 * the size field they actually wanted.
 */

import { memo, useRef } from 'react'
import { cx } from '@/lib/cx'
import { Glyph, NumCell } from '@/components/primitives'
import { nextRadioIndex, radioTabIndex } from '@/components/primitives/radio-group'
import type { Outcome } from '@/lib/decimal'

export interface OutcomeToggleProps {
  value: Outcome
  onChange: (outcome: Outcome) => void
  /** `63¢`, already formatted. `—` before the first `ticker` frame. */
  /**
   * The half's live price in cents, or `null` where the venue has not published
   * one — an empty book, a market that has not traded, a stale ticker.
   *
   * **`null`, not a dash.** The price is the secondary half of a control whose
   * primary job is choosing a side, so a placeholder for it is a mark with no
   * meaning sitting under the button the user is about to press. Omitting it
   * costs no layout: the half is a fixed `control-height-lg` and the label
   * simply centres in it. A `—` is the honest render for a number that has a
   * slot of its own — a table cell, a payout row — and this is not one.
   */
  upPrice: string | null
  downPrice: string | null
  disabled?: boolean
  className?: string
}

const HALF = {
  UP: {
    selected: 'bg-up-solid text-up-on-solid',
    idle: 'bg-bg-raised text-text-secondary hover:bg-up-bg hover:text-up-text',
    glyph: 'up',
  },
  DOWN: {
    selected: 'bg-down-solid text-down-on-solid',
    idle: 'bg-bg-raised text-text-secondary hover:bg-down-bg hover:text-down-text',
    glyph: 'down',
  },
} as const

export const OutcomeToggle = memo(function OutcomeToggle({
  value,
  onChange,
  upPrice,
  downPrice,
  disabled,
  className,
}: OutcomeToggleProps) {
  const groupRef = useRef<HTMLDivElement>(null)
  const outcomes = ['UP', 'DOWN'] as const
  const selectedIndex = outcomes.indexOf(value)

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label="outcome"
      onKeyDown={(event) => {
        if (disabled) return
        const to = nextRadioIndex(event.key, selectedIndex, outcomes.length)
        if (to === null) return
        event.preventDefault()
        const target = outcomes[to]
        if (!target) return
        onChange(target)
        groupRef.current?.querySelectorAll<HTMLButtonElement>('[role=radio]')[to]?.focus()
      }}
      className={cx(
        'grid grid-cols-2 overflow-hidden rounded-md border border-border-default',
        className,
      )}
    >
      {outcomes.map((outcome, index) => {
        const selected = value === outcome
        const style = HALF[outcome]
        const price = outcome === 'UP' ? upPrice : downPrice
        return (
          <button
            key={outcome}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={radioTabIndex(index, selectedIndex)}
            disabled={disabled}
            onClick={() => onChange(outcome)}
            data-outcome={outcome}
            data-selected={selected || undefined}
            className={cx(
              'flex h-[var(--ud-control-height-lg)] flex-col items-center justify-center gap-0_5',
              'transition-colors duration-[var(--ud-duration-fast)]',
              'focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-not-allowed',
              selected ? style.selected : style.idle,
            )}
          >
            <span className="flex items-center gap-1 text-body font-semibold">
              <Glyph name={style.glyph} label={null} />
              {outcome}
            </span>
            {price === null ? null : (
              <NumCell value={price} className="text-caption opacity-80" />
            )}
          </button>
        )
      })}
    </div>
  )
})
